interface MaterialRateWithParentIndex {
  // `id` widened to allow null so the yup-derived projectFormV2 row type
  // (`Maybe<string>` from `.notRequired()`) is structurally assignable.
  // The helper only inspects `id` for truthiness, so the widening is
  // behaviourally invisible.
  id?: string | null;
  isDeleted?: boolean | null;
  parentIndex?: number | null;
}

interface MaterialRateLookup {
  id?: string;
  material?: { id?: string; name?: string };
  unitOfMeasure?: string | null;
  site?: { id?: string; name?: string } | null;
  buyPrice?: number | null;
  sellPrice?: number | null;
}

interface MaterialTotalLookup {
  // Yup-derived form shapes (projectFormV2) widen these to allow null on
  // nullable autocomplete leaves; the legacy hand-written shape keeps them
  // strictly optional. The helper only reads via `?.`, so both work.
  material?: { id?: string | null } | null;
  unitOfMeasure?: { id?: string | null } | null;
}

interface MaterialRateFormRow {
  id: string;
  isDeleted: boolean;
  parentIndex: number;
  material: { id: string; name: string };
  unitOfMeasure: { id: string; name: string };
  materialRateSite: { id: string; name: string };
  materialRateBuyPrice: string | number;
  materialRateSellPrice: string | number;
}

export const mapInitialMaterialRatesToFormRows = (
  initialMaterialRates: MaterialRateLookup[],
  materialTotals: MaterialTotalLookup[],
  isCopyAndEdit: boolean,
): MaterialRateFormRow[] => {
  const materialTotalIndicesByMaterialId = materialTotals.reduce(
    (materialLookup, materialTotal, materialTotalIndex) => {
      const materialId = materialTotal.material?.id;

      if (!materialId) {
        return materialLookup;
      }

      const existingIndices = materialLookup.get(materialId) ?? [];
      existingIndices.push(materialTotalIndex);
      materialLookup.set(materialId, existingIndices);

      return materialLookup;
    },
    new Map<string, number[]>(),
  );
  const parentAssignmentCountByIndex = new Map<number, number>();

  return initialMaterialRates.reduce<MaterialRateFormRow[]>((mappedRates, rate) => {
    const matchingParentIndices = materialTotalIndicesByMaterialId.get(
      rate.material?.id ?? '',
    );
    const candidateParentIndices = matchingParentIndices ?? [];
    let parentIdx = -1;

    if (candidateParentIndices.length === 1) {
      parentIdx = candidateParentIndices[0] ?? -1;
    } else if (candidateParentIndices.length > 1) {
      const parentIndicesMatchingUom = candidateParentIndices.filter((index) => {
        return materialTotals[index]?.unitOfMeasure?.id === rate.unitOfMeasure;
      });
      const rankedParentCandidates = parentIndicesMatchingUom.length
        ? parentIndicesMatchingUom
        : candidateParentIndices;

      parentIdx = rankedParentCandidates.reduce<number>((bestIndex, currentIndex) => {
        const currentAssignments = parentAssignmentCountByIndex.get(currentIndex) ?? 0;
        const bestAssignments = parentAssignmentCountByIndex.get(bestIndex) ?? 0;

        if (currentAssignments < bestAssignments) {
          return currentIndex;
        }

        if (currentAssignments > bestAssignments) {
          return bestIndex;
        }

        return currentIndex < bestIndex ? currentIndex : bestIndex;
      }, rankedParentCandidates[0] ?? -1);
    }

    const hasMatchingParent = parentIdx >= 0;

    // A rate whose material isn't a project total is left untouched server-side, NOT
    // tombstoned for deletion. Previously edit mode kept these load-time orphans as
    // `isDeleted` tombstones, so the next (unrelated) save silently DELETEd them
    // (TRE-15257). Deletion only happens when the user explicitly removes a material
    // total in-session (see markMaterialRatesDeletedForSoftDeletedMaterialTotal /
    // remapMaterialRatesAfterMaterialTotalRemoval below). Copy mode always dropped them.
    if (!hasMatchingParent) {
      return mappedRates;
    }

    parentAssignmentCountByIndex.set(
      parentIdx,
      (parentAssignmentCountByIndex.get(parentIdx) ?? 0) + 1,
    );

    mappedRates.push({
      id: isCopyAndEdit ? '' : (rate.id ?? ''),
      isDeleted: false,
      parentIndex: parentIdx,
      material: { id: rate.material?.id ?? '', name: rate.material?.name ?? '' },
      unitOfMeasure: { id: rate.unitOfMeasure ?? '', name: rate.unitOfMeasure ?? '' },
      materialRateSite: { id: rate.site?.id ?? '', name: rate.site?.name ?? '' },
      materialRateBuyPrice:
        rate.buyPrice !== null && rate.buyPrice !== undefined
          ? rate.buyPrice.toFixed(2)
          : ('' as string | number),
      materialRateSellPrice:
        rate.sellPrice !== null && rate.sellPrice !== undefined
          ? rate.sellPrice.toFixed(2)
          : ('' as string | number),
    });

    return mappedRates;
  }, []);
};

export const remapMaterialRatesAfterMaterialTotalRemoval = <
  MaterialRateType extends MaterialRateWithParentIndex,
>(
  materialRates: MaterialRateType[],
  removedMaterialTotalIndex: number,
): MaterialRateType[] => {
  return materialRates.reduce<MaterialRateType[]>((nextMaterialRates, materialRate) => {
    const parentIndex = materialRate.parentIndex;

    if (parentIndex === removedMaterialTotalIndex) {
      if (materialRate.id) {
        nextMaterialRates.push({ ...materialRate, isDeleted: true });
      }

      return nextMaterialRates;
    }

    if (typeof parentIndex === 'number' && parentIndex > removedMaterialTotalIndex) {
      nextMaterialRates.push({ ...materialRate, parentIndex: parentIndex - 1 });

      return nextMaterialRates;
    }

    nextMaterialRates.push(materialRate);
    return nextMaterialRates;
  }, []);
};

export const markMaterialRatesDeletedForSoftDeletedMaterialTotal = <
  MaterialRateType extends MaterialRateWithParentIndex,
>(
  materialRates: MaterialRateType[],
  softDeletedMaterialTotalIndex: number,
): MaterialRateType[] => {
  return materialRates.reduce<MaterialRateType[]>((nextMaterialRates, materialRate) => {
    if (materialRate.parentIndex === softDeletedMaterialTotalIndex) {
      if (materialRate.id) {
        nextMaterialRates.push({ ...materialRate, isDeleted: true });
      }

      return nextMaterialRates;
    }

    nextMaterialRates.push(materialRate);
    return nextMaterialRates;
  }, []);
};
